import { Grid } from "@mui/material";
import { AutosaveFormTextField } from "components/Forms/AutosaveFormTextField";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useShowWasteStateRegistration } from "hooks/useWasteStateRegistration";
import { useCallback } from "react";
import { formatAmountInPounds } from "util/formatNumber";

import { WASTE_AMOUNT_TOOLTIP } from "../../../wasteStreamAmount";
import { formatWasteStreamProfileNumber } from "../formatWasteStreamProfileNumber";
import { prettyPrintWasteClassification } from "../prettyPrintWasteClassification";
import { prettyPrintWasteStreamRegistrationStatus } from "../prettyPrintWasteStreamRegistrationStatus";
import { useWasteStream, useUpdateWasteStream } from "./api";

interface WasteStreamInfoSectionProps {
  wasteStreamId: string;
  facilityState?: string;
}

// Two-up field layout for the drawer's narrow left column: one field per row on
// phones, two-up once there's room; the comment field spans the full width.
const FIELD = { xs: 12, sm: 6 } as const;
const FULL = { xs: 12 } as const;

// The single pinned field is the profile/description value that identified this
// waste stream — the precedence winner of calculateWasteStreamHashKey's profile
// slot (groupingProfile.source). Waste codes, form/source codes, and
// classification also feed the grouping key but are not individually pinned; the
// section-level info tooltip lists the full set.
const GROUPING_KEY_TOOLTIP =
  "The profile or shipping description value used to identify and group manifest line items into this waste stream.";

// Human-readable labels for grouping winners that have no dedicated field in the
// section (non-haz descriptions, document-sourced values). The fallback row at
// the bottom uses the source's name as its label so it reads like a real field.
const GROUPING_SOURCE_LABELS: Record<string, string> = {
  vendorNonHazWasteDescription: "Non-Haz Description",
  eManifestNonHazWasteDescription: "Non-Haz Description",
  documentNonHazWasteDescription: "Non-Haz Description",
  documentProfileDescription: "Profile Description",
  documentProfileNumber: "Profile #",
  documentDotPrintedInformation: "DOT Description",
};

export const WasteStreamInfoSection = ({
  wasteStreamId,
  facilityState,
}: WasteStreamInfoSectionProps) => {
  const { data: wasteStreamData } = useWasteStream(wasteStreamId);
  const [updateWasteStream] = useUpdateWasteStream();
  const wasteStream = wasteStreamData?.wasteStream;
  const showStateRegistration = useShowWasteStateRegistration(facilityState);
  // Whether this stream's waste counts toward the facility's generation number;
  // shown only for container-tracking tenants.
  const { featureFlags } = useFeatureFlags();
  const containerTrackingEnabled = Boolean(featureFlags[FeatureFlagKey.ContainerTracking]);

  const handleSaveComment = useCallback(
    async (value: string) => {
      await updateWasteStream({
        variables: {
          input: { id: wasteStreamId, comment: value },
        },
      });
    },
    [wasteStreamId, updateWasteStream]
  );

  // DOT is shown as a single value by topline precedence (e-Manifest > Vendor).
  const dotText = wasteStream?.eManifestDotDescription || wasteStream?.vendorDotDescription || "";

  // Which profile-slot field actually won grouping, and the winning value. A
  // single field carries the pin, and it shows the winning value so it always
  // reflects what grouped (the grouping DOT value can differ from the DOT
  // Description column normally shown). When the winner has no field of its own
  // — a profile/non-haz description or a document-sourced value — the "Grouped
  // on" fallback below renders it so there is always exactly one pin.
  const groupingProfile = wasteStream?.groupingProfile;
  const groupingSource = groupingProfile?.source;
  const groupingValue = groupingProfile?.value ?? "";
  const profileIsGroupingKey =
    groupingSource === "vendorProfileNumber" || groupingSource === "eManifestProfileNumber";
  const dotIsGroupingKey =
    groupingSource === "vendorDotPrintedInformation" ||
    groupingSource === "eManifestDotPrintedInformation";
  const profileDescriptionIsGroupingKey = groupingSource === "vendorProfileDescription";
  // Winner is a source with no dedicated field in this section (non-haz
  // descriptions, document-sourced values) — show it via the fallback field at
  // the bottom, labeled with the source's human name.
  const groupingHasNoDedicatedField =
    !!groupingSource &&
    !profileIsGroupingKey &&
    !dotIsGroupingKey &&
    !profileDescriptionIsGroupingKey;
  const groupingFallbackLabel =
    (groupingSource && GROUPING_SOURCE_LABELS[groupingSource]) || "Grouped on";

  return (
    <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
      <Grid size={FIELD}>
        <DisplayOnlyField
          text={formatAmountInPounds(wasteStream?.effectiveAmount)}
          label="Amount"
          infoTooltip={WASTE_AMOUNT_TOOLTIP}
          copyable
        />
      </Grid>
      <Grid size={FIELD}>
        <DisplayOnlyField
          text={formatWasteStreamProfileNumber({
            eManifestProfileNumber: wasteStream?.eManifestProfileNumber,
            vendorProfileNumber: wasteStream?.vendorProfileNumber,
          })}
          label="Profile #"
          copyable
          groupingKey={profileIsGroupingKey}
          groupingKeyTooltip={GROUPING_KEY_TOOLTIP}
        />
      </Grid>
      {profileDescriptionIsGroupingKey && (
        <Grid size={FULL}>
          <DisplayOnlyField
            text={groupingValue}
            label="Profile Description"
            copyable
            wrapValue
            groupingKey
            groupingKeyTooltip={GROUPING_KEY_TOOLTIP}
          />
        </Grid>
      )}
      <Grid size={FIELD}>
        <DisplayOnlyField
          text={prettyPrintWasteClassification(wasteStream?.classification)}
          label="Classification"
          copyable
        />
      </Grid>
      <Grid size={FIELD}>
        <DisplayOnlyField
          text={wasteStream?.federalWasteCodes?.join(", ") || ""}
          label="Federal Waste Code"
          copyable
        />
      </Grid>
      <Grid size={FIELD}>
        <DisplayOnlyField
          text={wasteStream?.stateWasteCodes?.join(", ") || ""}
          label={facilityState ? `${facilityState} Waste Code` : "State Waste Code"}
          copyable
        />
      </Grid>
      <Grid size={FIELD}>
        <DisplayOnlyField
          text={(wasteStream?.managementMethodCodes ?? []).join(", ")}
          label="Management Method"
          copyable
        />
      </Grid>
      <Grid size={FIELD}>
        <DisplayOnlyField text={wasteStream?.formCode || ""} label="Form Code" copyable />
      </Grid>
      <Grid size={FIELD}>
        <DisplayOnlyField text={wasteStream?.sourceCode || ""} label="Source Code" copyable />
      </Grid>
      <Grid size={FIELD}>
        <DisplayOnlyField
          text={wasteStream?.wasteMinimizationCode || ""}
          label="Waste Minimization Code"
          copyable
        />
      </Grid>
      {/* Reportable Status pairs two-up with Waste Minimization Code to keep the
          field rows balanced. */}
      <Grid size={FIELD}>
        <DisplayOnlyField
          text={wasteStream?.willNotReport ? "Not Reportable" : "Reportable"}
          label="Reportable Status"
          copyable
        />
      </Grid>
      {containerTrackingEnabled && (
        <Grid size={FIELD}>
          <DisplayOnlyField
            text={wasteStream?.countsTowardGeneration ? "Yes" : "No"}
            label="Counts Toward Generation"
            copyable
          />
        </Grid>
      )}
      <Grid size={FIELD}>
        <DisplayOnlyField
          text={
            wasteStream?.lastShipmentDate
              ? new Date(wasteStream.lastShipmentDate).toLocaleDateString(undefined, {
                  timeZone: "UTC",
                })
              : ""
          }
          label="Last Shipment (In Range)"
          copyable
        />
      </Grid>
      <Grid size={FIELD}>
        <DisplayOnlyField
          text={
            wasteStream?.lastShipmentDateAllTime
              ? new Date(wasteStream.lastShipmentDateAllTime).toLocaleDateString(undefined, {
                  timeZone: "UTC",
                })
              : ""
          }
          label="Last Shipment (All Time)"
          copyable
        />
      </Grid>
      {showStateRegistration && (
        <>
          <Grid size={FIELD}>
            <DisplayOnlyField
              text={wasteStream?.stateRegisteredId ?? ""}
              label="State Registered ID"
              copyable
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              text={prettyPrintWasteStreamRegistrationStatus(wasteStream?.stateRegistrationStatus)}
              label="Registration Status"
              copyable
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              text={
                wasteStream?.stateRegisteredAt
                  ? new Date(wasteStream.stateRegisteredAt).toLocaleDateString(undefined, {
                      timeZone: "UTC",
                    })
                  : ""
              }
              label="Registered At"
              copyable
            />
          </Grid>
        </>
      )}
      {/* DOT (proper shipping) description: full-width and last, just above the
          comment — long text that would otherwise crowd the two-up fields. */}
      <Grid size={FULL}>
        <DisplayOnlyField
          text={dotIsGroupingKey ? groupingValue : dotText}
          label="DOT Description"
          copyable
          wrapValue
          groupingKey={dotIsGroupingKey}
          groupingKeyTooltip={GROUPING_KEY_TOOLTIP}
        />
      </Grid>
      {/* Fallback grouping field: the winner has no dedicated field above, so
          render it here at the bottom, labeled with the source's human name. */}
      {groupingHasNoDedicatedField && (
        <Grid size={FULL}>
          <DisplayOnlyField
            text={groupingValue}
            label={groupingFallbackLabel}
            copyable
            wrapValue
            groupingKey
            groupingKeyTooltip={GROUPING_KEY_TOOLTIP}
          />
        </Grid>
      )}
      <Grid size={FULL}>
        <AutosaveFormTextField
          onSave={handleSaveComment}
          label="Note"
          defaultValue={wasteStream?.comment ?? ""}
          multiline
          rows={3}
        />
      </Grid>
    </Grid>
  );
};
